import { useEffect, useMemo } from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import { ListItem, LIST_VARIANTS } from "@puregym/ui";
import StatusBadge from "../common/statusBadge";
import labels from "../labels";
import { useGymSearch, useGymSearchState } from "../../context";
import {
  AlphabeticalListStyled,
  ListItemLink,
  GymCardGridWrapper,
  GymCardGrid,
  Overflow,
  MapHelpText,
} from "../../styles/GymSearchForm";
import GymCard from "../gymIndexPage/card/gymCard";

const MemoizedAlphabeticalList = ({ gyms, labels }) => {
  return useMemo(
    () => (
      <AlphabeticalListStyled
        data={gyms}
        aria-label={labels.gymsList}
        primaryKey="gymId"
        variant={LIST_VARIANTS.SIMPLE}
        renderListItem={(gym) => (
          <ListItem key={`gym-list-item-${gym.gymId}`}>
            <ListItemLink as={Link} to={`/gyms/${gym.slug}`}>
              <span>{gym.name}</span>
              <StatusBadge status={gym.status} aria-hidden />
            </ListItemLink>
          </ListItem>
        )}
      />
    ),
    [gyms, labels.gymsList]
  );
};

const renderGymCard = (gym) => (
  <ListItem key={`gym-list-item-${gym.gymId}`}>
    <GymCard gym={gym} variant={LIST_VARIANTS.SIMPLE} density="compact" />
  </ListItem>
);

/** @todo This can be simplified. We should improve the card from pure ui */
const ExperimentGymList = () => {
  const { gyms, filteredGyms } = useGymSearchState();
  const [ state ] = useGymSearch();
  const { searchTerm } = state;
  const gymsLength = gyms.length;
  const filteredGymsLength = filteredGyms.length;
  const gymsToShow = filteredGymsLength === 0 ? gyms : filteredGyms;
  const isFiltered =
    filteredGymsLength !== 0 && filteredGymsLength !== gymsLength;

  const { gymMiscData } = useStaticQuery(graphql`
    {
      gymMiscData {
        mapHelpText
      }
    }
  `);

  const sortedGyms = useMemo(() => {
    const collator = new Intl.Collator(process.env.GATSBY_LOCALE);
    // If we have a location then gyms have a `distanceFromSearchLocation`,
    // else we sort alphabetically.
    return [...gymsToShow].sort((a, b) => {
      if (a.distanceFromSearchLocation && b.distanceFromSearchLocation) {
        return a.distanceFromSearchLocation - b.distanceFromSearchLocation;
      }
      return collator.compare(a.name, b.name);
    });
  }, [gymsToShow]).filter((gym) => gym.name.toLowerCase().includes(searchTerm.toLowerCase()));  

  const gymCardGrid = (
    <GymCardGridWrapper>
      <GymCardGrid variant={LIST_VARIANTS.PLAIN} aria-label={labels.gymsList}>
        {sortedGyms.map((gym) => renderGymCard(gym))}
      </GymCardGrid>
    </GymCardGridWrapper>
  );

  return (
    <Overflow>
      {isFiltered && gymsToShow.length < 25 ? (
        gymCardGrid
      ) : (
        <MemoizedAlphabeticalList gyms={sortedGyms} labels={labels} />
      )}
      {gymMiscData.mapHelpText && (
        <MapHelpText
          dangerouslySetInnerHTML={{ __html: gymMiscData.mapHelpText }}
        />
      )}
    </Overflow>
  );
};

export default ExperimentGymList;

export { MemoizedAlphabeticalList };
